import React, { useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./ReportedIssueDetail.css";

import AppLoader from "../../../components/AppLoader/appLoader";
import {
  actionMap,
  formatDuration,
  formatLabel,
  mapStatusToBootstrap,
  mapStatusToDotColor,
} from "../../../components/Constants/Constants";
import { ArrowLeft, Plus, RotateCw } from "lucide-react";
import { Button, Modal } from "react-bootstrap";

import { useReportedIssueDetails } from "../../../customHooks/ReportedIssues/ReportedIssues/useReportedIssueDetails";


const GroupedActionAccordion = ({
  groupedActions,
  activeTab,
  statusFilter,
  openPhase,
  setOpenPhase,
  renderGroupedActionItem,
}) => {
  // 1. Pre-filter the grouped actions based on activeTab and statusFilter
  const filteredGroups = groupedActions
    .map((group) => {
      const actionsFiltered = group.actions.filter(
        (action) =>
          (activeTab === "all" || action.action_type === "RESPONDENT") &&
          (statusFilter === "all" || action?.description?.code === statusFilter)
      );

      return {
        ...group,
        actions: actionsFiltered, // Keep only the matching actions
      };
    })
    .filter((group) => group.actions.length > 0); // 2. Remove groups (phases) that now have zero actions

  if (filteredGroups.length === 0) {
    return (
      <div className="text-center text-muted py-3">
        No actions found matching the current filters.
      </div>
    );
  }

  // 3. Render only the remaining, relevant phases
  return filteredGroups.map((group, groupIdx) => {
    const groupKey = `${group.phase}-${group.level}`;
    return (
      <div
        key={group.phase || groupIdx}
        className="accordion-item mb-3 border-0 shadow-sm rounded-3 overflow-hidden"
      >
        {/* Accordion Header */}
        <div
          onClick={() => setOpenPhase(openPhase === groupKey ? null : groupKey)}
          className={`d-flex justify-content-between align-items-center px-3 py-2 fw-semibold text-dark border rounded-2
          ${
            openPhase === groupKey
              ? "bg-primary text-white"
              : "bg-light text-dark"
          }
          cursor-pointer transition-all`}
          style={{
            cursor: "pointer",
            transition: "background-color 0.2s ease, color 0.2s ease",
          }}
        >
          <span>
            Phase: <strong>{group.phase}</strong> ({group.level})
          </span>
          <i
            className={`bi ${
              openPhase === groupKey ? "bi-chevron-down" : "bi-chevron-right"
            }`}
          ></i>
        </div>

        {/* Accordion Content */}
        {openPhase === groupKey && (
          <div className="accordion-body border-start border-end border-bottom bg-white py-3 px-3">
            {/* Map and render the filtered actions using the passed-in render function */}
            {group.actions.map(renderGroupedActionItem)}
          </div>
        )}
      </div>
    );
  });
};
function ReportedIssueDetails() {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const {
   issueData,
        loading,
        error,
        refetch: getIssueDetails,
        activeDetailsTab,
        setActiveDetailsTab,
        handleBack,
        handleProvideInfoClick,
        handleSave,
        isModalOpen,
        closeModal,
        formData,
        setFormData,
        selectedRequest,
        setSelectedRequest,
        handleEscalate,
        handleCloseIssue,
        handleStatusRequest,
        handleFileChange,
        // New exports:
        pendingResolutionGroups,
        selectedResolutions,
        handleResolutionSelection,
        handleResolutionSubmit,
        isResolutionGroupValid,
        isResolutionSubmitting,
  } = useReportedIssueDetails({ issueId });

  const [activeTab, setActiveTab] = useState("all");

  const [statusFilter, setStatusFilter] = useState("all");
  const [showResolutionHierarchy, setShowResolutionHierarchy] = useState(false);
  const [openPhase, setOpenPhase] = useState(null);

  const actions = issueData?.actions || [];

  const groupedActions = useMemo(() => {
    // 1. Get the pre-grouped array directly from the API response
    const sourceGroups = issueData?.ModifiedActions;

    if (!sourceGroups || sourceGroups.length === 0) {
      return [];
    }

    // 2. Sort the actions *inside* each group from newest to oldest
    return sourceGroups.map((group) => ({
      ...group, // Keeps the correct phase and level (e.g., "PROCESSING", "GRIEVANCE")
      actions: group.actions
        .slice() // Create a copy to sort safely
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
    }));
  }, [issueData?.ModifiedActions]);

  const issue = issueData?.issue_details;
  const infoRequests = issueData?.PendingInfoRequests || [];

  const pendingResolutions = issueData?.PendingResolutions || [];

  const isAlreadyEscalated =
    issueData?.issue_details?.status?.toUpperCase() === "ESCALATED" ||
    (issueData?.actions || []).some(
      (action) => action.description?.code === "ESCALATED"
    );

  const categoryValue = issue?.category?.length
    ? (() => {
        // Group by type
        const grouped = issue.category.reduce((acc, cat) => {
          if (!acc[cat.type]) acc[cat.type] = [];
          acc[cat.type].push(cat.id);
          return acc;
        }, {});

        // Convert to display string
        return Object.entries(grouped)
          .map(([type, ids]) => `${type} (${ids.join(", ")})`)
          .join(" → ");
      })()
    : "N/A";

  const categories = issue?.category || [];

  // Map over the categories to create a new object for each one
  const categoryItems = categories.map((cat) => ({
    // Use the category 'type' (e.g., "ORDER") as the label
    label: cat.type,
    // Use the category 'id' as the value
    value: cat.id || "N/A",
  }));

  const issueImages = useMemo(() => {
    const images = issue?.issue_description?.images || [];
    const media = issue?.issue_description?.media || [];

    const allUrls = [
      ...images.map((img) => img.url),
      ...media.map((m) => m.url),
    ].filter(Boolean); // Filter out any null/undefined URLs

    // Return unique URLs
    return [...new Set(allUrls)];
  }, [issue?.issue_description]);

  // Classification
  const issueClassification = [
    { label: "Level", value: issue?.level || "N/A" },
    ...categoryItems,
    { label: "Issue Code", value: issue?.issue_description?.code || "N/A" },
    {
      label: "Short Description",
      value: issue?.issue_description?.short_desc || "N/A",
    },
    {
      label: "Long Description",
      value: issue?.issue_description?.long_desc || "N/A",
    },
    {
      label: "Created At",
      value: new Date(issue?.created_at).toLocaleString(),
    },
    {
      label: "Updated At",
      value: new Date(issue?.updated_at).toLocaleString(),
    },
  ];

  const complainantDetails = {
    name: issueData?.issue_details?.complainant_details?.name || "N/A",
    org: issueData?.issue_details?.complainant_details?.org_name || "N/A",
    phone: issueData?.issue_details?.complainant_details?.phone || "N/A",
    email: issueData?.issue_details?.complainant_details?.email || "N/A",
  };
  const sourceDetails = {
    name: issueData?.issue_details?.source_details?.name || "N/A",
    org: issueData?.issue_details?.source_details?.org_name || "N/A",
    phone: issueData?.issue_details?.source_details?.phone || "N/A",
    email: issueData?.issue_details?.source_details?.email || "N/A",
  };

  const timingInfo = [
    {
      label: "Expected Response Time",
      value: formatDuration(issue?.expected_response_time),
    },
    {
      label: "Expected Resolution Time",
      value: formatDuration(issue?.expected_resolution_time),
    },
    {
      label: "First Responded At",
      value: issue?.first_responded_at
        ? new Date(issue.first_responded_at).toLocaleString()
        : "N/A",
    },
    {
      label: "Actual Resolution Time",
      value: issue?.resolved_at
        ? new Date(issue.resolved_at).toLocaleString()
        : "N/A",
    },

    { label: "Total Actions", value: actions.length },
  ];

    const renderGroupedActionItem = useCallback((action, idx) => {
      const actionCode = action.description?.code;
      const map = actionMap[actionCode] || {
        type: actionCode?.replace(/_/g, " ") || "UNKNOWN",
        class: "respondent-action",
      };
  
      const actorName = action.actor?.type
        ? action.actor.type
            .replace(/_/g, " ") // replace underscores with spaces
            .replace(/\b\w/g, (c) => c.toUpperCase()) // capitalize first letter of each word
        : "INTERFACING NP";
      const actorContact = action.actor?.info?.contact?.phone;
      const actionTime = new Date(action.updatedAt).toLocaleString();
      const actoremail = action.actor?.info?.contact?.email;
      // Get dot color from centralized function
      const dotColor = mapStatusToDotColor(map.type);
      return (
        <div
          key={action.id}
          className={`timeline-item ${map.class}`}
          style={{ "--dot-color": dotColor }}
        >
          <div className="action-header">
            <div className={`action-type ${mapStatusToBootstrap(map.type)}`}>
              {map.type}
            </div>
            <div className="action-time">{actionTime}</div>
          </div>
          {/* <div className="action-desc">{actionDesc}</div> */}
          <div className="action-by">
            <span className="action-person">{actorName}</span>
            <span className="action-contact">
              {actoremail} , {actorContact}
            </span>
          </div>
        </div>
      );
    }, []);
  if (loading) return <AppLoader />;
  if (error) return <div className="text-danger">Error: {error}</div>;
  return (
    <>
      <div className="container-fluid mt-2">
        {/* Header */}
        <div className="header">
          <div className="header-left flex flex-col">
            <div>
              <button
                className="btn btn-outline-secondary me-3"
                onClick={handleBack}
                style={{
                  color: "white",
                  borderColor: "white",
                }}
              >
                <ArrowLeft size={18} className="me-1" />
                Back
              </button>
              Issue #{issue?.id}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="btn btn-outline-info btn-sm"
              aria-label="Request Status"
              onClick={handleStatusRequest}
              disabled={loading}
            >
              {loading ? "Requesting..." : "Request Status"}
            </button>
            <button
              type="button"
              className={`closeButton mx-2 px-3 ${
                issue?.status === "CLOSED" ? "disabled" : ""
              }`}
              aria-label="Close Issue"
              title={
                issue?.status === "CLOSED" ? "Issue Closed" : "Close Issue"
              }
              onClick={() => handleCloseIssue("Closing issue")}
              disabled={
                loading ||
                issue?.status === "CLOSED" ||
                issue?.disable?.disableCloseIssue
              }
            >
              {issue?.status === "CLOSED"
                ? "Issue Closed"
                : loading
                ? "Closing..."
                : "Close Issue"}
            </button>
            <span
              className={`inline-flex items-center px-3 py-2 rounded ${mapStatusToBootstrap(
                issue?.status
              )}`}
              style={{ gap: "6px" }}
            >
              <span className="text-sm font-medium">
                {(issue?.status || "N/A").replace("_", " ")}
              </span>
            </span>
          </div>
        </div>

        <div className="main-layout">
          <div className="content-area">
            {/* Issue Classification */}
            <div className="info-section">
              <div className="section-header">Issue Classification</div>
              <div className="compact-grid">
                {issueClassification.map((item) => (
                  <div className="info-item" key={item.label}>
                    <div className="info-label">{item.label}</div>
                    <div
                      className="info-value truncate-text"
                      title={item.value}
                    >
                      {item.value}
                    </div>
                  </div>
                ))}
                {issueImages.length > 0 && (
                  <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                    {" "}
                    {/* Span full width */}
                    <div className="info-label">Attached Media</div>
                    <div className="info-value d-flex flex-wrap gap-2 mt-1">
                      {issueImages.map((url, index) => (
                        <a
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          title="Click to view full image and download"
                          style={{ textDecoration: "none" }}
                        >
                          <img
                            src={url}
                            alt={`Issue Media ${index + 1}`}
                            style={{
                              width: "60px",
                              height: "60px",
                              objectFit: "cover",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              cursor: "pointer",
                            }}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Complainant */}
            <div className="info-section mb-3">
              <div className="section-header mb-3">Complainant Details</div>
              <div className="d-flex flex-wrap border p-3 rounded">
                {/* Name */}
                <div className="me-5 info-item">
                  <div className="info-label">Name</div>
                  <div className="info-value">{complainantDetails.name}</div>
                </div>

                {/* Organization */}
                <div className="me-5 info-item">
                  <div className="info-label">Organization</div>
                  <div className="info-value">{complainantDetails.org}</div>
                </div>

                {/* Phone */}
                <div className="me-5 info-item">
                  <div className="info-label">Phone</div>
                  <div className="info-value">{complainantDetails.phone}</div>
                </div>

                {/* Email */}
                <div className="me-5 info-item">
                  <div className="info-label">Email</div>
                  <div className="info-value">{complainantDetails.email}</div>
                </div>
              </div>
            </div>

            {/* --- Consumer Details Section --- */}
            <div className="info-section mb-3">
              <div className="section-header mb-3">Consumer Details</div>
              <div className="d-flex flex-wrap border p-3 rounded">
                {/* Name */}
                <div className="me-5 info-item">
                  <div className="info-label">Name</div>
                  <div className="info-value">{sourceDetails.name}</div>
                </div>

                {/* Organization */}
                <div className="me-5 info-item">
                  <div className="info-label">Organization</div>
                  <div
                    className="info-value truncate-text"
                    title={sourceDetails.org}
                  >
                    {sourceDetails.org}
                  </div>
                </div>

                {/* Phone */}
                <div className="me-5 info-item">
                  <div className="info-label">Phone</div>
                  <div className="info-value">{sourceDetails.phone}</div>
                </div>

                {/* Email */}
                <div className="me-5 info-item">
                  <div className="info-label">Email</div>
                  <div
                    className="info-value truncate-text"
                    title={sourceDetails.email}
                  >
                    {sourceDetails.email}
                  </div>
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="section-header">Response & Resolution Timing</div>
              <div className="compact-grid">
                {timingInfo.map((item) => (
                  <div className="info-item" key={item.label}>
                    <div className="info-label">{item.label}</div>
                    <div className="info-value">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="details-panel">
              <div className="details-tabs">
                <div
                  className={`details-tab ${
                    activeDetailsTab === "info" ? "active" : ""
                  }`}
                  onClick={() => setActiveDetailsTab("info")}
                >
                  Information Requests
                </div>
                <div
                  className={`details-tab ${
                    activeDetailsTab === "resolution" ? "active" : ""
                  }`}
                  onClick={() => setActiveDetailsTab("resolution")}
                >
                  Resolution Options
                </div>
              </div>

              <div className="details-content">
                {activeDetailsTab === "info" && (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead>
                        <tr>
                          <th>Request No.</th>
                          <th>Short Description</th>
                          <th>Updated At</th>
                          <th>Actor Details</th>
                          <th>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {infoRequests.length > 0 ? (
                          infoRequests.map((req, index) => {
                            const descriptor = req.descriptor || {};
                            const actorInfo = req.actor_details?.info || {};
                            const person = actorInfo.person || {};
                            const contact = actorInfo.contact || {};
                            const org = actorInfo.org || {};
                            const infoProvided = req.info_provided;

                            const isInfoProvided =
                              infoProvided?.descriptor?.code ===
                              "INFO_PROVIDED";

                            return (
                              <tr key={req.id || index}>
                                <td>{index + 1}</td>
                                <td>{descriptor.short_desc || "—"}</td>
                                <td>
                                  {new Date(req.updated_at).toLocaleString()}
                                </td>
                                <td>
                                  <div>
                                    <div>
                                      <strong>{person.name || "N/A"}</strong>
                                    </div>
                                    {contact.email && (
                                      <div className="text-muted small">
                                        {contact.email}
                                      </div>
                                    )}
                                    {contact.phone && (
                                      <div className="text-muted small">
                                        {contact.phone}
                                      </div>
                                    )}
                                    {org.name && (
                                      <div className="text-muted small">
                                        {org.name}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <Button
                                    variant={
                                      isInfoProvided
                                        ? "success"
                                        : "outline-primary"
                                    }
                                    size="sm"
                                    onClick={() => handleProvideInfoClick(req)}
                                    disabled={
                                      isInfoProvided ||
                                      issueData?.disable?.disableReqInfo
                                    }
                                    title={
                                      isInfoProvided
                                        ? "Information has already been provided for this request."
                                        : "Click to provide information."
                                    }
                                  >
                                    {isInfoProvided
                                      ? "Info Provided ✓"
                                      : "Provide Info"}
                                  </Button>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan="6"
                              className="text-center text-muted pt-3"
                            >
                              No information requests found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              {activeDetailsTab === "resolution" && (
    <div className="resolution-options-list">
        <form onSubmit={handleResolutionSubmit}>
            
            {/* --- COMMON HEADER (Shown once for all items) --- */}
            {pendingResolutionGroups.length > 0 && (() => {
                const group = pendingResolutionGroups[0];
                const proposedAt = new Date(group.parent_details?.proposed_at).toLocaleString();
                const proposedBy =
                    group.parent_details?.proposed_by?.info?.person?.name ||
                    group.parent_details?.proposed_by?.info?.org?.name ||
                    "N/A";

                return (
                    <h5 className="mb-3">
                        <span className="text-muted small ms-1">
                            (Proposed By: {proposedBy} | On: {proposedAt})
                        </span>
                    </h5>
                );
            })()}

            {pendingResolutionGroups.length > 0 ? (
                pendingResolutionGroups.map((group) => {
                    const parentId = group.id;
                    const isSelected = !!selectedResolutions[parentId];

                    return (
                        <div
                            key={parentId}
                            className={`border rounded p-2 mb-3 ${
                                isSelected
                                    ? selectedResolutions[parentId].accepted === "true"
                                        ? "bg-success-subtle border-success"
                                        : "bg-danger-subtle border-danger"
                                    : "bg-light"
                            }`}
                        >
                            {/* Removed proposedBy section from here */}

                            {/* Resolution Radio Options */}
                            <div className="d-flex flex-wrap gap-4">
                                {group.options.map((opt) => {
                                    const optionId = opt.id;
                                    const isChecked =
                                        selectedResolutions[parentId]?.selected_resolution_id === optionId;

                                    const optionCode = opt.description.code.replace(/_/g, " ");
                                    const amount = opt.description.amount ? ` (₹${opt.description.amount})` : "";
                                    const optionLabel = `${optionCode.toUpperCase()} ${opt.description.short_desc}${amount}`;

                                    return (
                                        <div key={optionId} className="form-check form-check-inline">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name={`resolution-item-${parentId}`}
                                                id={`res-opt-${optionId}`}
                                                value={optionId}
                                                checked={isChecked}
                                                onChange={() =>
                                                    handleResolutionSelection(parentId, optionId, "true")
                                                }
                                                disabled={isResolutionSubmitting}
                                            />
                                            <label className="form-check-label" htmlFor={`res-opt-${optionId}`}>
                                                {optionLabel}
                                            </label>
                                        </div>
                                    );
                                })}

                                {/* Reject ALL */}
                                <div className="form-check form-check-inline">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name={`resolution-item-${parentId}`}
                                        id={`res-reject-${parentId}`}
                                        value="REJECT"
                                        checked={
                                            selectedResolutions[parentId]?.accepted === "false"
                                        }
                                        onChange={() =>
                                            handleResolutionSelection(parentId, parentId, "false")
                                        }
                                        disabled={isResolutionSubmitting}
                                    />
                                    <label
                                        className="form-check-label text-danger fw-bold"
                                        htmlFor={`res-reject-${parentId}`}
                                    >
                                        REJECT ALL
                                    </label>
                                </div>
                            </div>
                        </div>
                    );
                })
            ) : (
                <p className="text-center text-muted pt-3">
                    No pending resolution options found.
                </p>
            )}

            {/* --- Common Validation Message (optional) --- */}
            {!isResolutionGroupValid() && pendingResolutionGroups.length > 0 && (
                <div className="text-danger small mt-2 mb-3">
                    * Please select an option OR reject all for every item.
                </div>
            )}

            {/* Submit Button */}
            {pendingResolutionGroups.length > 0 && (
                <div className="text-end pt-3 border-top">
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={!isResolutionGroupValid() || isResolutionSubmitting}
                    >
                        {isResolutionSubmitting ? "Submitting..." : "Submit Selections"}
                    </Button>
                </div>
            )}
        </form>
    </div>
)}

              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="timeline">
            <div className="timeline-header">Action Timeline</div>
            <div className="timeline-filters flex items-center gap-3 mb-3">
              {/* Tabs */}
              {!showResolutionHierarchy && (
                <div className="timeline-tabs flex gap-2">
                  <div
                    className={`tab ${activeTab === "all" ? "active" : ""}`}
                    onClick={() => setActiveTab("all")}
                  >
                    All
                  </div>

                  <div
                    className={`tab ${
                      activeTab === "respondent" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("respondent")}
                  >
                    Respondent
                  </div>

                  {/* In src/pages/Issues/IssueDetails.jsx */}

                  {/* In your Timeline Filters section */}
                  <div className="dropdown-filters">
                    <select
                      className="form-control"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      {[
                        ...new Set(
                          (actions || [])
                            .filter((a) =>
                              activeTab === "respondent"
                                ? a.action_type === "RESPONDENT"
                                : true
                            )
                            .map((a) => a.description?.code)
                            .filter(Boolean)
                        ),
                      ].map((status) => (
                        <option key={status} value={status}>
                          {formatLabel(status)}
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setStatusFilter("all")}
                      disabled={statusFilter === "all"}
                      style={{ padding: "0.15rem 0.5rem" }}
                      title="Clear Status Filter"
                    >
                      <RotateCw size={"16px"} />
                    </Button>
                  </div>
                </div>
              )}

              {/* Resolution toggle button */}
              <div className="dropdown-filters flex gap-2">
                <button
                  className={`btn btn-outline-primary btn-sm ${
                    showResolutionHierarchy ? "active" : ""
                  }`}
                  onClick={() => setShowResolutionHierarchy((prev) => !prev)}
                >
                  {showResolutionHierarchy
                    ? "Hide Resolution Hierarchy"
                    : "Show by Resolution"}
                </button>
              </div>
            </div>

            <div
              className="timeline"
              style={{ maxHeight: "800px", overflowY: "auto" }}
            >
              {showResolutionHierarchy
                ? issueData?.resolutions?.map((itemBlock, itemIdx) => (
                    <div
                      key={itemBlock.item_id || itemIdx}
                      className="item-resolution-block mb-4"
                    >
                      <span
                        style={{ fontSize: "1rem" }}
                        className="item-header font-semibold mb-3"
                      >
                        Item ID:{" "}
                        <span className="text-primary">
                          {itemBlock.item_id}
                        </span>
                      </span>

                      {/* 🟣 LOOP THROUGH RESOLUTIONS OF THIS ITEM */}
                      {itemBlock.resolutions?.map((res, idx) => {
                        const isMultiOption = !!res.parent_details;
                        const finalResolution =
                          res.resolution_history?.[0] || {};
                        const hasFinalOutcome = !!finalResolution.status;
                        const isAccepted =
                          finalResolution.status === "RESOLUTION_ACCEPTED";

                        const parent = res.parent_details || res;
                        const options = res.options || [];

                        const proposedBy =
                          parent.proposed_by?.info?.person?.name || "Unknown";
                        const proposedByOrg =
                          parent.proposed_by?.info?.org?.name || "Unknown Org";
                        const proposedAt = parent.proposed_at
                          ? new Date(parent.proposed_at).toLocaleDateString()
                          : "N/A";

                        return (
                          <div key={res.id || idx} className="timeline-item">
                            <div
                              className={`timeline-dot ${
                                isAccepted ? "accepted-dot" : "rejected-dot"
                              }`}
                            ></div>

                            <div className="card">
                              <div className="card-header flex items-center justify-between">
                                <h4 className="text-base font-semibold">
                                  Resolution #{idx + 1}
                                </h4>

                                {hasFinalOutcome && (
                                  <span
                                    className={`status ${
                                      isAccepted ? "accepted" : "rejected"
                                    }`}
                                  >
                                    {isAccepted ? "✅ Accepted" : "❌ Rejected"}
                                  </span>
                                )}
                              </div>

                              <div className="card-body">
                                <div className="meta-info mb-2">
                                  <span>
                                    <strong>Proposed By:</strong> {proposedBy} (
                                    {proposedByOrg})
                                  </span>
                                  <span>
                                    <strong>Proposed On:</strong> {proposedAt}
                                  </span>
                                  <span>
                                    <strong>Hierarchy Type:</strong>{" "}
                                    {isMultiOption
                                      ? "Multi-Option Proposal"
                                      : "Direct Proposal"}
                                  </span>
                                </div>

                                {isMultiOption ? (
                                  <div className="options-list">
                                    <h4>Options Presented:</h4>
                                    {options.map((opt) => (
                                      <div key={opt.id} className="option">
                                        <p>
                                          {formatLabel(opt.description.code)}
                                        </p>
                                        <div className="detail">
                                          {opt.description.short_desc && (
                                            <>{opt.description.short_desc}.</>
                                          )}
                                          {opt.description.amount && (
                                            <>
                                              {" "}
                                              Amount: ₹{opt.description.amount}.
                                            </>
                                          )}
                                          {opt.description
                                            .expected_delivery && (
                                            <>
                                              {" "}
                                              Expected Delivery:{" "}
                                              {new Date(
                                                opt.description.expected_delivery
                                              ).toLocaleDateString()}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="proposal-details">
                                    <h4>Proposal Details:</h4>
                                    <div className="option">
                                      <p>Offer {res.description.code}</p>
                                      <div className="detail">
                                        {res.description.short_desc && (
                                          <>{res.description.short_desc}.</>
                                        )}
                                        {res.description.amount && (
                                          <>
                                            {" "}
                                            Amount: ₹{res.description.amount}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {hasFinalOutcome && (
                                <div
                                  className={`card-footer ${
                                    isAccepted
                                      ? "footer-accepted"
                                      : "footer-rejected"
                                  }`}
                                >
                                  Final Outcome:{" "}
                                  {isAccepted ? "Accepted the" : "Rejected the"}{" "}
                                  <strong>
                                    {finalResolution.description?.code}
                                  </strong>{" "}
                                  {finalResolution.description?.amount && (
                                    <strong>
                                      ₹{finalResolution.description.amount}
                                    </strong>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))
                   : (<GroupedActionAccordion
                  groupedActions={groupedActions}
                  activeTab={activeTab}
                  statusFilter={statusFilter}
                  openPhase={openPhase}
                  setOpenPhase={setOpenPhase}
                  renderGroupedActionItem={renderGroupedActionItem}
                />
              )}
            </div>
            <div className="card-footer d-flex justify-content-end gap-2 pt-2">
              <button
                className={`btn btn-sm ${
                  loading ||
                  isAlreadyEscalated ||
                  issueData?.disable?.disableEscalateIssue
                    ? "btn-secondary"
                    : "btn-warning"
                }`}
                onClick={() => handleEscalate()}
                disabled={
                  loading ||
                  isAlreadyEscalated ||
                  issueData?.disable?.disableEscalateIssue
                }
                title={
                  isAlreadyEscalated
                    ? "This issue has already been escalated."
                    : "Click to escalate this issue."
                }
              >
                {loading
                  ? "Escalating..."
                  : isAlreadyEscalated
                  ? "Escalate Issue"
                  : "Escalate Issue"}
              </button>
            </div>
          </div>
        </div>
        <Modal show={isModalOpen} onHide={closeModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Provide Information Request</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <form>
              <div className="mb-3">
                <label htmlFor="short_desc" className="form-label">
                  Short Description <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="short_desc"
                  className="form-control"
                  required
                  value={formData.short_desc}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      short_desc: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="mb-3">
                <label htmlFor="long_desc" className="form-label">
                  Detailed Description
                </label>
                <textarea
                  id="long_desc"
                  className="form-control"
                  rows={4}
                  value={formData.long_desc}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      long_desc: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="mb-3">
                <label htmlFor="image_file" className="form-label">
                  Images (Multi-select)
                </label>
                <input
                  type="file"
                  id="image_file"
                  className="form-control"
                  multiple
                  onChange={handleFileChange} // Use the new handler
                />
              </div>
            </form>
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
            >
              Save
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}

export default ReportedIssueDetails;
